import { useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Skeleton, Card } from "antd";
import { LiaArrowRightSolid } from "react-icons/lia";

import { useThunk } from "../../hooks/useThunk";
import { getPaymentDetailsByUnitIdForAdmin } from "../../store/thunks/paymentThunks";
import StatCardGroup from "../../components/Common/StatCardGroup";
import Table from "../../components/Table/LandLordTable";
import Loader from "../../components/Common/Loader";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const ViewPaymentsPage = () => {
  const navigate = useNavigate();
  const { unitId } = useParams();
  const query = useQuery();
  const userId = query.get("userId");

  const [doFetch, isFetching, fetchError] = useThunk(getPaymentDetailsByUnitIdForAdmin);

  const { info, payments, loading, error } = useSelector(
    (state) => state.payment.unitPaymentDetails
  );

  useEffect(() => {
    if (unitId) {
      doFetch({ unitId, userId });
    }
  }, [unitId, userId, doFetch]);
  const tenantInfoCards = [
    { label: "Tenant Name", value: info?.tenantName || "None" },
    { label: "Property Name", value: info?.propertyName || "None" },
    { label: "Unit ID / Name", value: `${info?.unitId || ''} / ${info?.unitName || ''}`.trim() || "None" },
    {
      label: "Monthly Rent",
      value: info?.monthlyRent
        ? `UGX ${info.monthlyRent.toLocaleString()}`
        : "0"
    },
    {
      label: "Deposit Received",
      value: info?.depositReceived ? `UGX ${info.depositReceived.toLocaleString()}` : "0" 
    },
    { label: "Paid-Through Date", value: info?.paidThroughDate ? new Date(info.paidThroughDate).toLocaleDateString() : "None" },
    { label: "Outstanding Days", value: info?.outstandingDays ? `${info.outstandingDays} days` : "None" },
  ];
  const columns = [
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
    {
      title: "Amount Paid",
      dataIndex: "amount",
      key: "amount",
      render: (val) => (typeof val === "number" ? `${val.toFixed(2)} UGX` : val ?? "-"),
    },
    {
      title: "Covered Period",
      //dataIndex: "coveredPeriod",
      key: "coveredPeriod",
      render: (_, record) => (
        <span className="flex flex-row items-center gap-2">
          {record.rentCoveredFrom
            ? new Date(record.rentCoveredFrom).toLocaleDateString()
            : "-"}{" "}
          <LiaArrowRightSolid className="mx-1 mt-0" />
          {record.rentCoveredTo
            ? new Date(record.rentCoveredTo).toLocaleDateString()
            : "-"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex justify-center p-4 sm:p-6 md:p-[30px] font-inter">
      <div className="w-full lg:w-[1080px] xl:w-full">
              {(loading || isFetching) && (
        <div className="flex justify-center items-center absolute left-72 z-50 inset-0 ">
        <Loader color="#4772F1" />
        </div>
      )}

        {/* <h2 className="text-xl font-semibold">{title}</h2>*/}
        {/* <Button onClick={() => navigate(-1)}>Back</Button>*/}
        <div className="p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white rounded-[10px] shadow-[0_4px_4px_0_#0000000F]">
          <StatCardGroup items={tenantInfoCards} />
        </div>

        <div className="mt-[30px]">
            <div className="w-full overflow-x-auto">
              <Table
                rowKey={(r, i) => r.paymentDate + "_" + i}
                dataSource={payments || []}
                columns={columns}
                hasSubTable={false}
                pagination={{ pageSize: 10, showSizeChanger: false }}
              />
            </div>
      {(error || fetchError) && (
            <div className="mt-4 text-red-600">
          {(typeof error === "string" && error) || (typeof fetchError === "string" && fetchError) || "Failed to load payments."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewPaymentsPage;
